import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Sun, Moon, Monitor, Menu, LogOut, Building2, ChevronDown, AlertTriangle, CheckSquare, Inbox, ShieldCheck, Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { api, setActiveCompany } from "../lib/api";
import { TwoFactorModal } from "./TwoFactorModal";
import { SUPPORTED_LANGS } from "../i18n";
import { useAccessRequests, AccessRequestList } from "./AccessRequests";
import { VoiceGuideButton } from "./VoiceGuideButton";
import { AppSwitcher } from "./AppSwitcher";
import { SupportTicketButton } from "./SupportTicketButton";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "à l'instant";
  if (d < 3600) return `il y a ${Math.floor(d / 60)} min`;
  if (d < 86400) return `il y a ${Math.floor(d / 3600)} h`;
  return `il y a ${Math.floor(d / 86400)} j`;
}

export default function Topbar({ onMenu }) {
  const { theme, mode, setMode } = useTheme();
  const { user, meta, logout, refresh } = useAuth();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { requests: accessRequests, respond: respondAccess, busy: accessBusy } = useAccessRequests();
  const [companies, setCompanies] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [twofaOpen, setTwofaOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const notifRef = useRef(null);
  const themeRef = useRef(null);
  const langRef = useRef(null);
  const [sq, setSq] = useState("");
  const [sres, setSres] = useState(null);
  const [sopen, setSopen] = useState(false);
  const searchRef = useRef(null);

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("pp_lang", lng);
    setLangOpen(false);
    if (user) api.savePreferences({ language: lng }).catch(() => {});
  };

  const changeTheme = (m) => {
    setMode(m);
    setThemeOpen(false);
    if (user) api.savePreferences({ theme_preference: m }).catch(() => {});
  };

  useEffect(() => {
    const h = (e) => {
      if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (sq.trim().length < 2) { setSres(null); return; }
    const t = setTimeout(() => { api.search(sq).then((r) => { setSres(r); setSopen(true); }).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [sq]);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSopen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const goSearch = (path) => { setSopen(false); setSq(""); setSres(null); nav(path); };
  const searchCount = sres ? (sres.employees.length + sres.chantiers.length + sres.projects.length) : 0;

  useEffect(() => {
    if (user) api.myCompanies().then(setCompanies).catch(() => {});
  }, [user]);

  const loadNotifs = () => {
    api.notifications().then((d) => {
      setNotifs(d.notifications || []);
      const lastSeen = localStorage.getItem("pp_notif_seen") || "";
      const u = (d.notifications || []).filter((n) => (n.time || "") > lastSeen).length;
      setUnread(u);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openNotifs = () => {
    setNotifOpen((v) => !v);
    if (!notifOpen) {
      localStorage.setItem("pp_notif_seen", new Date().toISOString());
      setUnread(0);
    }
  };

  const switchCompany = async (cid) => {
    setActiveCompany(cid);
    await refresh();
    window.location.reload();
  };

  const NICON = { anomaly: AlertTriangle, approval: CheckSquare };
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <header className="topbar" data-testid="topbar">
      <button className="top-btn mobile-toggle" onClick={onMenu} data-testid="menu-toggle"><Menu size={18} /></button>
      <div className="search-box" style={{ maxWidth: 380, position: "relative" }} ref={searchRef} data-testid="global-search">
        <Search size={17} />
        <input placeholder={t("common.search")} value={sq} onChange={(e) => setSq(e.target.value)}
          onFocus={() => sres && setSopen(true)} data-testid="global-search-input" />
        {sopen && sres && (
          <div className="search-results" data-testid="search-results">
            {searchCount === 0 && <div className="search-empty">{t("topbar.noResults")} « {sq} »</div>}
            {sres.employees.length > 0 && <div className="search-group">{t("topbar.employees")}</div>}
            {sres.employees.map((e) => (
              <button key={e.id} className="search-item" onClick={() => goSearch(`/employes/${e.id}`)} data-testid="search-result-emp">
                <span className="search-item-name">{e.name}</span><span className="search-item-sub">{e.sub}</span>
              </button>
            ))}
            {sres.chantiers.length > 0 && <div className="search-group">Chantiers</div>}
            {sres.chantiers.map((c) => (
              <button key={c.id} className="search-item" onClick={() => goSearch(`/chantiers`)}>
                <span className="search-item-name">{c.name}</span><span className="search-item-sub">{c.sub}</span>
              </button>
            ))}
            {sres.projects.length > 0 && <div className="search-group">Projets</div>}
            {sres.projects.map((p) => (
              <button key={p.id} className="search-item" onClick={() => goSearch(`/projets`)}>
                <span className="search-item-name">{p.name}</span><span className="search-item-sub">{p.sub}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {companies.length > 1 && (
        <div style={{ position: "relative" }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowMenu((v) => !v)} data-testid="company-switcher">
            <Building2 size={15} color="var(--gold)" /> {meta.company || "Entreprise"} <ChevronDown size={14} />
          </button>
          {showMenu && (
            <div className="card" style={{ position: "absolute", top: 46, left: 0, width: 240, padding: 6, zIndex: 60 }}>
              {companies.map((c) => (
                <div key={c.id} className="nav-item" style={{ margin: 2 }} onClick={() => switchCompany(c.id)} data-testid="company-option">
                  <Building2 size={15} className="nav-ico" /> <span style={{ fontSize: 13 }}>{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1 }} />
      <AppSwitcher />
      <SupportTicketButton />
      <div style={{ position: "relative" }} ref={langRef}>
        <button className="top-btn" onClick={() => setLangOpen((v) => !v)} data-testid="lang-toggle" title={t("lang.label")}>
          <Globe size={18} />
        </button>
        {langOpen && (
          <div className="card" style={{ position: "absolute", top: 46, right: 0, width: 170, padding: 6, zIndex: 70 }} data-testid="lang-menu">
            {SUPPORTED_LANGS.map((lng) => (
              <div key={lng} className="nav-item" style={{ margin: 2, justifyContent: "flex-start", gap: 9 }}
                onClick={() => changeLang(lng)} data-testid={`lang-option-${lng}`}>
                <span style={{ fontSize: 13, flex: 1 }}>{t(`lang.${lng}`)}</span>
                {i18n.language === lng && <Check size={14} color="var(--gold)" />}
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: "relative" }} ref={themeRef}>
        <button className="top-btn" onClick={() => setThemeOpen((v) => !v)} data-testid="theme-toggle" title={t("topbar.appearance")}>
          {mode === "system" ? <Monitor size={18} /> : theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        {themeOpen && (
          <div className="card" style={{ position: "absolute", top: 46, right: 0, width: 184, padding: 6, zIndex: 70 }} data-testid="theme-menu">
            {[
              ["light", t("topbar.themeLight"), Sun],
              ["dark", t("topbar.themeDark"), Moon],
              ["system", t("topbar.themeAuto"), Monitor],
            ].map(([val, label, Icon]) => (
              <div key={val} className="nav-item" style={{ margin: 2, justifyContent: "flex-start", gap: 9 }}
                onClick={() => changeTheme(val)} data-testid={`theme-option-${val}`}>
                <Icon size={15} className="nav-ico" /> <span style={{ fontSize: 13, flex: 1 }}>{label}</span>
                {mode === val && <Check size={14} color="var(--gold)" />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: "relative" }}>
        <VoiceGuideButton />
      </div>

      <div style={{ position: "relative" }} ref={notifRef}>
        <button className="top-btn" data-testid="notifications-btn" onClick={openNotifs} title="Notifications">
          <Bell size={18} />
          {(unread + accessRequests.length) > 0 && <span className="notif-count" data-testid="notif-count">{(unread + accessRequests.length) > 9 ? "9+" : (unread + accessRequests.length)}</span>}
        </button>
        {notifOpen && (
          <div className="card notif-panel" data-testid="notif-panel">
            <div className="notif-head"><Inbox size={15} color="var(--gold)" /> {t("topbar.notifications")} <span style={{ color: "var(--muted)", fontWeight: 500 }}>({notifs.length + accessRequests.length})</span></div>
            <div className="notif-list">
              <AccessRequestList requests={accessRequests} respond={respondAccess} busy={accessBusy} compact />
              {notifs.length === 0 && accessRequests.length === 0 && <div className="notif-empty">{t("topbar.noNotifications")}</div>}
              {notifs.map((n) => {
                const Icon = NICON[n.type] || Bell;
                const color = n.level === "Élevé" ? "#ff6b6b" : n.level === "Moyen" ? "var(--gold)" : "var(--gold-light)";
                return (
                  <div key={n.id} className="notif-item" data-testid="notif-item"
                    onClick={() => { setNotifOpen(false); nav(n.link || "/tableau"); }}>
                    <div className="notif-ico" style={{ color }}><Icon size={15} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-detail">{n.detail}</div>
                    </div>
                    <div className="notif-time">{timeAgo(n.time)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <span className="role-chip" data-testid="role-chip">{meta.roleLabel || "—"}</span>
      <button className="top-btn" onClick={() => setTwofaOpen(true)} data-testid="security-btn" title={t("topbar.security")}><ShieldCheck size={18} /></button>
      <div className="avatar" title={user?.name} data-testid="user-avatar">{initials}</div>
      <button className="top-btn" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" title={t("topbar.logout")}><LogOut size={18} /></button>
      <TwoFactorModal open={twofaOpen} onClose={() => setTwofaOpen(false)} />
    </header>
  );
}
